import { useEffect, useRef, useState } from 'react';
import { CircleCheck, ShieldAlert, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import type { JunkScanPage } from '@dts/core';
import { api, errorMessage } from '@/lib/api';
import { useFolders } from '@/lib/queries';
import { queryClient } from '@/lib/query-client';
import { Button, Modal, Spinner } from '@/components/ui';

type Moved = JunkScanPage['junked'][number];
type Phase = 'idle' | 'running' | 'waiting' | 'done' | 'stopped' | 'error';

const MAX_RETRIES = 3; // per page, when the junk model is rate limited

/**
 * "Check Inbox for junk": runs the junk filter over everything already in the Inbox, page by page. Senders in your
 * contacts or on your safe list stay without asking the AI; block-listed senders go to Junk; the rest are checked by
 * the junk model. Shows progress, can be stopped, and can put everything it moved back.
 */
export function JunkScanDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const inbox = useFolders().data?.find((f) => f.name === 'Inbox');
  const [phase, setPhase] = useState<Phase>('idle');
  const [checked, setChecked] = useState(0);
  const [moved, setMoved] = useState<Moved[]>([]);
  const [unchecked, setUnchecked] = useState(0);
  const [message, setMessage] = useState('');
  const [total, setTotal] = useState(0);
  const stop = useRef(false);

  useEffect(() => {
    if (open) {
      setPhase('idle');
      setChecked(0);
      setMoved([]);
      setUnchecked(0);
    } else {
      stop.current = true;
    }
  }, [open]);

  const run = async () => {
    stop.current = false;
    setTotal(inbox?.total ?? 0);
    setPhase('running');
    let continuation: string | undefined;
    let retries = 0;
    try {
      for (;;) {
        if (stop.current) return setPhase('stopped');
        const page = await api.junkScan(continuation, retries >= MAX_RETRIES);
        setChecked((n) => n + page.checked);
        if (page.junked.length) {
          setMoved((m) => [...page.junked, ...m]);
          void queryClient.invalidateQueries();
        }
        if (page.retryAfterSeconds) {
          retries++;
          setPhase('waiting');
          await new Promise((r) => setTimeout(r, page.retryAfterSeconds! * 1000));
          setPhase('running');
          continue;
        }
        if (retries >= MAX_RETRIES) setUnchecked((n) => n + page.errors);
        retries = 0;
        continuation = page.continuation;
        if (page.done) break;
      }
      setPhase('done');
      void queryClient.invalidateQueries();
    } catch (e) {
      setMessage(errorMessage(e));
      setPhase('error');
    }
  };

  const undo = async () => {
    const ids = moved.map((m) => m.id);
    try {
      for (let i = 0; i < ids.length; i += 200) await api.batch(ids.slice(i, i + 200), 'move', 'Inbox');
      toast.success(`Moved ${ids.length} back to the Inbox`);
      setMoved([]);
      void queryClient.invalidateQueries();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const busy = phase === 'running' || phase === 'waiting';
  const pct = total ? Math.min(100, Math.round((checked / total) * 100)) : 0;

  return (
    <Modal open={open} onClose={() => (busy ? (stop.current = true) : onClose())} title="Check Inbox for junk" width={520}>
      <div className="px-6 pb-6 pt-5">
        <div className="mb-4 flex items-center gap-3 rounded-xl bg-bg/50 px-4 py-3 text-[13.5px]">
          <ShieldAlert className="size-4 text-muted" />
          <span className="flex-1">Inbox</span>
          <span className="tabular-nums">{(inbox?.total ?? 0).toLocaleString()} emails</span>
          <span className="tabular-nums text-muted">· {(inbox?.unread ?? 0).toLocaleString()} unread</span>
        </div>

        {phase === 'idle' && (
          <>
            <p className="text-[13px] text-muted">
              Clears clutter out of your Inbox: newsletters, promotions, cold outreach (recruiters, sales, webinar pitches)
              and spam move to the Junk folder. Receipts, account and security notices, shipping updates and real
              conversations stay. Senders in your contacts or on your safe list are kept without asking the AI; blocked
              senders go straight to Junk. Emails you marked as not junk are left alone, and no senders are blocked.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <Button variant="ghost" onClick={onClose}>Cancel</Button>
              <Button variant="primary" onClick={() => void run()} disabled={!inbox?.total} icon={<ShieldAlert className="size-4" />}>Check {(inbox?.total ?? 0).toLocaleString()} emails</Button>
            </div>
          </>
        )}

        {phase !== 'idle' && (
          <>
            <div className="h-2 overflow-hidden rounded-full bg-line">
              <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${phase === 'done' ? 100 : pct}%` }} />
            </div>
            <div className="mt-2 flex items-center gap-2 text-[12.5px] text-muted">
              {busy && <Spinner className="size-3.5" />}
              <span className="flex-1">
                {phase === 'waiting' ? 'The junk model is busy; trying again in a few seconds…'
                  : phase === 'done' ? 'Finished.'
                  : phase === 'stopped' ? 'Stopped.'
                  : phase === 'error' ? message
                  : 'Checking…'}
              </span>
              <span className="tabular-nums">{checked.toLocaleString()} checked</span>
            </div>
            <div className="mt-4 text-[15px] font-semibold">
              {moved.length.toLocaleString()} moved to Junk
              {unchecked > 0 && <span className="ml-2 text-[12.5px] font-normal text-muted">({unchecked} couldn't be checked)</span>}
            </div>
            {moved.length > 0 && (
              <div className="mt-2 max-h-56 space-y-1 overflow-y-auto rounded-xl bg-bg/50 px-3 py-2 text-[12.5px]">
                {moved.slice(0, 200).map((m) => (
                  <div key={m.id} className="flex gap-2">
                    <span className="w-40 shrink-0 truncate font-medium">{m.from || 'Unknown sender'}</span>
                    <span className="truncate text-muted">{m.subject || '(no subject)'}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              {busy ? (
                <Button variant="soft" onClick={() => (stop.current = true)}>Stop</Button>
              ) : (
                <>
                  {moved.length > 0 && <Button variant="ghost" onClick={() => void undo()} icon={<Undo2 className="size-4" />}>Put them back</Button>}
                  <Button variant="primary" onClick={onClose} icon={<CircleCheck className="size-4" />}>Done</Button>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
